import React, { useState, useEffect, useCallback } from 'react';
import './styles.css'; // Import the styles.css file
import {
  AppData,
  AppStatus,
  BusinessValueMetrics,
  CalculatedAreas,
  Dimension,
  ImprovementArea,
  Relationship,
  UIState,
  ImprovementsData,
  MaturityLevel,
} from './types';
import { D3BubbleChart } from './components/D3BubbleChart';
import { useStarRatingCalculation } from './hooks/useStarRatingCalculation';

// Helper functions
const getScaleFactor = (scale: string): number => ({ Peak: 1.5, Emerging: 1.0, Weak: 0.5 }[scale] || 1.0);
const getCircleSize = (impact: number): number => Math.max(36, Math.min(108, impact * 1.08));

// New function to calculate responsive font size based on circle size and text length
const getCircleFontSize = (circleSize: number, textLength: number): number => {
  // Base calculation: smaller circle or longer text = smaller font
  const baseFontSize = circleSize / 7; // Slightly larger base size (was /8)

  // More aggressive scaling for short text to allow bigger fonts when possible
  // Short text (under 10 chars) gets a boost
  const shortTextBonus = textLength < 10 ? 1.2 : 1.0;

  // Adjust for text length - reduce size for longer text
  const lengthFactor = Math.max(0.5, 1 - textLength / 30); // More gradual reduction

  // Allow larger maximum size (16px) for short text that fits
  return Math.max(9, Math.min(16, baseFontSize * lengthFactor * shortTextBonus));
};

// Maturity helper functions
const getMaturityColor = (maturityLevel: MaturityLevel | null): string => {
  // Softer colors based on maturity
  switch (maturityLevel) {
    case 'Weak':
      return '#ffe5e5'; // softer red
    case 'Emerging':
      return '#ffecd9'; // softer orange
    case 'Established':
      return '#fffce6'; // softer yellow
    case 'Peak':
      return '#e8f5e8'; // softer green
    case null:
      return '#f5f5f5'; // light gray for null maturity
    default:
      return '#ffffff';
  }
};

const getMaturityTextColor = (maturityLevel: string | null): string => {
  switch (maturityLevel) {
    case 'Weak':
      return '#dc2626';
    case 'Emerging':
      return '#f97316'; // brighter orange with less red (was #ea580c)
    case 'Established':
      return '#eab308'; // brighter, more yellow color (was #ca8a04)
    case 'Peak':
      return '#16a34a';
    case null:
    default:
      return '#333333';
  }
};

// Function to render the plant growth icon with appropriate styling for maturity levels
const renderMaturityIcon = (maturityLevel: string): JSX.Element => {
  const color = getMaturityTextColor(maturityLevel);
  const level = getMaturityLevelNumber(maturityLevel);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Vertical stem */}
      <path d="M12 22V2" />

      {/* Arrow at top */}
      <path d="M8 6L12 2L16 6" />

      {/* Leaves - simple shapes like in the reference image */}
      {level >= 1 && (
        <>
          <path d="M12 18L7 15L12 12" />
          <path d="M12 18L17 15L12 12" />
        </>
      )}

      {level >= 2 && (
        <>
          <path d="M12 14L7 11L12 8" />
          <path d="M12 14L17 11L12 8" />
        </>
      )}

      {level >= 3 && (
        <>
          <path d="M12 10L8 8L12 6" />
          <path d="M12 10L16 8L12 6" />
        </>
      )}
    </svg>
  );
};

// Helper functions for relationship strength indicators
const getRelationshipStrengthWidth = (weight: number): number => {
  if (!weight) return 2;
  // Typical weights are between 0.05 and 0.15
  if (weight >= 0.13) return 5; // Very strong relationship
  if (weight >= 0.11) return 4; // Strong relationship
  if (weight >= 0.09) return 3; // Medium relationship
  return 2; // Weak relationship
};

const getRelationshipStrengthShadow = (weight: number): string => {
  return 'none'; // Remove all shadows
};

const maturityLevels = ['Weak', 'Emerging', 'Established', 'Peak'];

// Helper function to convert star rating to scale
const getScaleFromStars = (stars: number): string => {
  if (stars >= 4) return 'Peak';
  if (stars >= 2) return 'Emerging';
  return 'Weak';
};

// Helper function to convert maturity level to number
const getMaturityLevelNumber = (maturityLevel: string | null): number | null => {
  switch (maturityLevel) {
    case 'Weak':
      return 1;
    case 'Emerging':
      return 2;
    case 'Established':
      return 3;
    case 'Peak':
      return 4;
    case null:
      return null;
    default:
      return 1;
  }
};

// Function to get the appropriate maturity icon path based on maturity level
const getMaturityIconPath = (maturityLevel: string): string => {
  // Convert maturity level name to lowercase for the filename
  const filename = maturityLevel.toLowerCase();

  // Return the path with the maturity level name
  return `/${filename}.png`;
};

// Function to get embedded SVG image for each maturity level
const getMaturityIconSvg = (maturityLevel: string): string => {
  const color = getMaturityTextColor(maturityLevel);
  const level = getMaturityLevelNumber(maturityLevel);

  // Base SVG with just stem and arrow (Weak - Level 1)
  const weakSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M12 22V4" />
      <path d="M8 8L12 4L16 8" />
    </svg>
  `;

  // SVG with stem, arrow, and one pair of leaves (Emerging - Level 2)
  const emergingSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M12 22V4" />
      <path d="M8 8L12 4L16 8" />
      <path d="M12 16L6 13L12 10" />
      <path d="M12 16L18 13L12 10" />
    </svg>
  `;

  // SVG with stem, arrow, and two pairs of leaves (Established - Level 3)
  const establishedSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M12 22V4" />
      <path d="M8 8L12 4L16 8" />
      <path d="M12 18L6 15L12 12" />
      <path d="M12 18L18 15L12 12" />
      <path d="M12 12L7 9.5L12 7" />
      <path d="M12 12L17 9.5L12 7" />
    </svg>
  `;

  // SVG with stem, arrow, and three pairs of leaves (Peak - Level 4)
  const peakSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M12 22V4" />
      <path d="M8 8L12 4L16 8" />
      <path d="M12 20L5 17L12 14" />
      <path d="M12 20L19 17L12 14" />
      <path d="M12 14L6 11.5L12 9" />
      <path d="M12 14L18 11.5L12 9" />
      <path d="M12 9L8 7.5L12 6" />
      <path d="M12 9L16 7.5L12 6" />
    </svg>
  `;

  // Return the appropriate SVG based on level
  switch (level) {
    case 1:
      return `data:image/svg+xml;utf8,${encodeURIComponent(weakSvg)}`;
    case 2:
      return `data:image/svg+xml;utf8,${encodeURIComponent(emergingSvg)}`;
    case 3:
      return `data:image/svg+xml;utf8,${encodeURIComponent(establishedSvg)}`;
    case 4:
      return `data:image/svg+xml;utf8,${encodeURIComponent(peakSvg)}`;
    default:
      return `data:image/svg+xml;utf8,${encodeURIComponent(weakSvg)}`;
  }
};

// Add utility functions before the BusinessValueVisualization component
const BusinessValueVisualization: React.FC = () => {
  // Add debugging logs
  console.log('Component rendering - BusinessValueVisualization');

  // Initialize the star rating calculation hook
  const { recalculateStarRatings, recalculateStarRatingsForArea } = useStarRatingCalculation();

  // State for managing data
  const [data, setData] = useState<AppData>({
    dimensions: [],
    improvementAreas: {},
    relationships: [],
    businessValueMetrics: undefined,
  });

  // State for UI interactions
  const [ui, setUi] = useState<UIState>({
    highlightedDimension: null,
    highlightedAreas: [],
    relationshipWeights: {},
    showingPopup: false,
    currentPopup: null,
    tooltipVisible: null,
    debugMode: false, // Add debug mode state
  });

  // State for calculated impact values
  const [calculatedAreas, setCalculatedAreas] = useState<CalculatedAreas>({});

  // State for top improvement opportunities
  const [topOpportunities, setTopOpportunities] = useState<any[]>([]);

  // State for application status
  const [status, setStatus] = useState<AppStatus>({
    loading: true,
    error: null,
  });

  // State for tracking hovered area
  const [hoveredArea, setHoveredArea] = useState(null);

  // State for currently active dialog
  const [activeDialog, setActiveDialog] = useState<string | null>(null);

  // Add totalImpact to the component state
  const [totalImpact, setTotalImpact] = useState<number>(0);

  // State for import modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importFileName, setImportFileName] = useState<string | null>(null);
  const [importFileContent, setImportFileContent] = useState<string | null>(null);

  // Handler for file input change
  const handleImportFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setImportError(null);
    setImportFile(null);
    setImportFileName(null);
    setImportFileContent(null);
    if (!file) return;
    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      setImportError('Please select a valid JSON file.');
      return;
    }
    setImportFile(file);
    setImportFileName(file.name);
    // Read file content for later submission
    const reader = new FileReader();
    reader.onload = e => {
      setImportFileContent(e.target?.result as string);
    };
    reader.readAsText(file);
  };

  // Handler for submit button in modal
  const handleImportSubmit = () => {
    setImportError(null);
    if (!importFile || !importFileContent) {
      setImportError('Please select a JSON file to import.');
      return;
    }
    try {
      const json = JSON.parse(importFileContent);
      // json is expected to be in the format of maturity-assessment-results.json
      // { "Testing": { category: ..., assessment: { maturity_level: ... } } }
      // We'll match area names (e.g., "Testing") and set their maturityLevel
      const newImprovementAreas = { ...data.improvementAreas };
      let foundAny = false;
      Object.entries(json).forEach(([areaName, areaData]: any) => {
        const maturityLevel = areaData?.assessment?.maturity_level;
        // Allow null/undefined maturity levels - they will be handled by calculations

        // Find the area in all quadrants
        for (const [quadrant, areas] of Object.entries(newImprovementAreas)) {
          const idx = (areas as ImprovementArea[]).findIndex(a => a.name === areaName);
          if (idx !== -1) {
            (newImprovementAreas[quadrant] as ImprovementArea[])[idx] = {
              ...areas[idx],
              maturityLevel: maturityLevel || null,
              maturityScore: maturityLevel ? getMaturityLevelNumber(maturityLevel) : null,
            };
            foundAny = true;
          }
        }
      });
      if (!foundAny) {
        setImportError('No matching improvement areas found in the uploaded file.');
      } else {
        // Update improvement areas with new maturity levels
        const updatedData = { ...data, improvementAreas: newImprovementAreas };

        // Use the hook to recalculate star ratings based on the new maturity levels
        const finalData = recalculateStarRatings(updatedData);

        // Set the complete updated data
        setData(finalData);

        setShowImportModal(false);
        setImportError(null);
        setImportFile(null);
        setImportFileName(null);
        setImportFileContent(null);
      }
    } catch (err) {
      setImportError('Invalid JSON file. Please check the format.');
    }
  };

  // Handler for closing the modal (reset state)
  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportError(null);
    setImportFile(null);
    setImportFileName(null);
    setImportFileContent(null);
  };

  // Fetch all data
  useEffect(() => {
    console.log('Data loading effect running');

    // Fetch data from JSON files
    const fetchData = async () => {
      try {
        console.log('Fetching data from JSON files');

        // Fetch business value metrics - updating paths to try different locations
        const businessValueResponse = await fetch('/business-value-metrics.json');
        if (!businessValueResponse.ok) {
          throw new Error(`Failed to fetch business-value-metrics.json: ${businessValueResponse.status}`);
        }
        const businessValueMetricsData = await businessValueResponse.json();
        console.log('Loaded business value metrics:', businessValueMetricsData);

        // Fetch improvement areas
        const improvementsResponse = await fetch('/improvements.json');
        if (!improvementsResponse.ok) {
          throw new Error(`Failed to fetch improvements.json: ${improvementsResponse.status}`);
        }
        const improvementsData = await improvementsResponse.json();
        console.log('Loaded improvements data:', Object.keys(improvementsData));

        // Fetch relationships
        const relationshipsResponse = await fetch('/relationships.json');
        if (!relationshipsResponse.ok) {
          throw new Error(`Failed to fetch relationships.json: ${relationshipsResponse.status}`);
        }
        const relationshipsData = await relationshipsResponse.json();
        console.log('Loaded relationships:', relationshipsData.length);

        // Process the data
        processLoadedData(businessValueMetricsData, improvementsData, relationshipsData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setStatus({ loading: false, error: err.message });
      }
    };

    fetchData();
  }, []);

  // Function to process loaded data and prepare it for the app
  const processLoadedData = (
    businessValueMetricsData: BusinessValueMetrics,
    improvementsData: ImprovementsData,
    relationshipsData: Relationship[],
  ) => {
    try {
      // Convert business value metrics to dimensions format for compatibility
      const dimensions: Dimension[] = [];
      businessValueMetricsData.categories.forEach(category => {
        category.metrics.forEach(metric => {
          // Add a default currentRating if not present
          if (!metric.currentRating) {
            metric.currentRating = 3; // Default to middle rating
          }

          dimensions.push({
            name: metric.name,
            stars: metric.currentRating,
            scale: getScaleFromStars(metric.currentRating),
            category: category.name,
            description: metric.description,
          });
        });
      });

      console.log(`Converted ${dimensions.length} metrics to dimensions format`);

      // Update the improvements data with maturity levels if not present
      const improvementsWithMaturity = {};
      Object.entries(improvementsData).forEach(([quadrant, quadrantData]) => {
        // Extract the items array from the new quadrant data structure
        const areas: ImprovementArea[] = quadrantData.items || [];

        improvementsWithMaturity[quadrant] = areas.map((area: ImprovementArea) => {
          if (!area.maturityLevel || !area.maturityScore) {
            // Default to 'Emerging' if maturity level isn't defined
            return {
              ...area,
              maturityLevel: area.maturityLevel || 'Emerging',
              maturityScore: area.maturityScore || 2,
            };
          }
          return area;
        });
      });

      // Extract quadrant descriptions
      const quadrantDescriptions = {};
      Object.entries(improvementsData).forEach(([quadrant, quadrantData]) => {
        quadrantDescriptions[quadrant] = quadrantData.description || '';
      });

      // Set the data
      setData({
        dimensions: dimensions,
        improvementAreas: improvementsWithMaturity,
        relationships: relationshipsData,
        businessValueMetrics: businessValueMetricsData,
        quadrantDescriptions: quadrantDescriptions,
      });
      setStatus({ loading: false, error: null });
    } catch (error) {
      console.error('Error processing data:', error);
      setStatus({ loading: false, error: 'Error processing data: ' + error.message });
    }
  };

  // Calculate impact values when dimensions or improvement areas change
  useEffect(() => {
    if (status.loading || status.error || !data.dimensions.length) return;

    const { dimensions, improvementAreas, relationships } = data;
    const allAreas = [];
    const results: CalculatedAreas = {};

    // Basic values
    const minValue = 0.1,
      maxValue = 1.0;
    const totalStars = dimensions.reduce((sum, d) => sum + d.stars, 0);
    const avgRatio = totalStars / dimensions.length / 5;

    // Process areas
    Object.entries(improvementAreas).forEach(([quadrant, areas]) => {
      results[quadrant] = (areas as ImprovementArea[]).map((area: ImprovementArea) => {
        // Skip areas with null maturity levels in the impact calculation
        if (area.maturityLevel === null || area.maturityScore === null) {
          return {
            ...area,
            quadrant,
            rawScore: 0,
            calculatedImpact: 0,
            opportunityScore: 0,
            effectiveWeight: 0,
            topContributors: [],
            allContributors: [],
          };
        }

        // Calculate contribution from each dimension
        let baseValue = minValue + (maxValue - minValue) * avgRatio;
        let adjustment = 0;
        const contributions = {};

        dimensions.forEach(dim => {
          const factor = getScaleFactor(dim.scale) * (dim.stars / 5);
          const rel = relationships.find(r => r.dimension === dim.name && r.area === area.name);
          const weight = rel ? rel.weight : 0.01;
          const contrib = weight * factor;

          if (contrib >= 0.02) contributions[dim.name] = contrib;
          adjustment += contrib;
        });

        // Apply maturity-dependent weight modifier
        const maturityLevel = area.maturityLevel || 'Weak';
        const maturityModifier = area.maturityWeightModifiers ? area.maturityWeightModifiers[maturityLevel] || 0 : 0;

        // Apply baseWeight with maturity modifier but ensure it's capped at 1.0
        const effectiveBaseWeight = Math.min((area.baseWeight || 0.5) + maturityModifier, 1.0);

        // Final calculation with maturity-dependent weight
        let finalValue = Math.min(baseValue * (1 + adjustment) * effectiveBaseWeight, 1.0);
        if (totalStars === dimensions.length) finalValue = 0.1;
        if (totalStars === dimensions.length * 5) finalValue = 1.0;

        // Top contributors
        const topContribs = Object.entries(contributions)
          .sort((a, b) => (b[1] as number) - (a[1] as number))
          .slice(0, 3);
        const totalContrib = Object.entries(contributions).reduce((sum, [_, v]) => sum + (v as number), 0);

        // Get all contributors
        const allContribs = Object.entries(contributions).sort((a, b) => (b[1] as number) - (a[1] as number));

        const areaObj = {
          ...area,
          quadrant,
          rawScore: finalValue,
          calculatedImpact: finalValue,
          opportunityScore: finalValue * (4 - (area.maturityScore || 1)),
          effectiveWeight: effectiveBaseWeight,
          topContributors: topContribs.map(([name, val]) => ({
            dimension: name,
            percentage: Math.round(((val as number) / totalContrib) * 100),
          })),
          allContributors: allContribs.map(([name, val]) => ({
            dimension: name,
            percentage: Math.round(((val as number) / totalContrib) * 100),
          })),
        };

        // Only add to allAreas if the area has a maturity level
        allAreas.push(areaObj);
        return areaObj;
      });
    });

    // Normalize to ensure total is 100% - only consider areas with non-null maturity levels
    const areasWithMaturity = allAreas.filter(area => area.maturityLevel !== null && area.maturityScore !== null);
    const totalRawScore = areasWithMaturity.reduce((sum, a) => sum + (a.rawScore || 0), 0);

    Object.keys(results).forEach(quadrant => {
      results[quadrant] = (results[quadrant] as ImprovementArea[]).map(area => {
        // Only normalize areas with non-null maturity levels
        if (area.maturityLevel === null || area.maturityScore === null) {
          return area; // Keep as is (impact = 0)
        }

        return {
          ...area,
          calculatedImpact: totalRawScore > 0 ? Math.round(((area.rawScore || 0) / totalRawScore) * 100) : 0,
        };
      });
    });

    // Calculate and set total impact across all areas with maturity levels
    const areasWithMaturityForTotal = allAreas.filter(
      area => area.maturityLevel !== null && area.maturityScore !== null,
    );
    const newTotalImpact = areasWithMaturityForTotal.reduce((sum, a) => sum + (a.calculatedImpact || 0), 0);
    setTotalImpact(newTotalImpact);
    console.log(`Total Impact: ${newTotalImpact}%`); // For verification

    setCalculatedAreas(results);
  }, [data, status]);

  // Calculate top opportunities based on impact and maturity
  const calculateTopOpportunities = () => {
    if (!calculatedAreas || Object.keys(calculatedAreas).length === 0) {
      return []; // Return empty array if no data is available yet
    }

    const allAreas = [];

    // Collect all areas with their calculated impact and maturity
    Object.entries(calculatedAreas).forEach(([quadrant, areas]) => {
      (areas as ImprovementArea[]).forEach((area: ImprovementArea) => {
        // Skip areas with null maturity levels - they should not be included in top opportunities
        if (area.maturityLevel === null || area.maturityScore === null) {
          return;
        }

        allAreas.push({
          name: area.name,
          quadrant: quadrant,
          calculatedImpact: area.calculatedImpact,
          maturityLevel: area.maturityLevel,
          maturityScore: area.maturityScore || 1,
          opportunityScore: area.opportunityScore || 0,
        });
      });
    });

    // Sort by opportunity score (highest first)
    const sortedAreas = allAreas.sort((a, b) => b.opportunityScore - a.opportunityScore);

    // Return top 5 opportunities
    return sortedAreas.slice(0, 5);
  };

  // Function to check and enforce boundary conditions for all dimensions and areas
  const enforceGlobalBoundaryConditions = data => {
    const { dimensions, improvementAreas, businessValueMetrics } = data;
    let newData = { ...data };

    // Check if all dimensions are at minimum (1 star)
    const allDimensionsMin = dimensions.every(dim => dim.stars === 1);

    // Check if all dimensions are at maximum (5 stars)
    const allDimensionsMax = dimensions.every(dim => dim.stars === 5);

    // If either boundary condition is met, update all improvement areas
    if (allDimensionsMin || allDimensionsMax) {
      const targetMaturity = allDimensionsMin ? 'Weak' : 'Peak';
      const targetScore = allDimensionsMin ? 1 : 4;

      const updatedAreas = {};

      // Update all areas to match the boundary condition, but skip areas with null maturity levels
      Object.entries(improvementAreas).forEach(([quadrant, areas]) => {
        updatedAreas[quadrant] = (areas as ImprovementArea[]).map((area: ImprovementArea) => {
          // Skip areas with null maturity levels - they should remain unchanged
          if (area.maturityLevel === null || area.maturityScore === null) {
            return area;
          }

          return {
            ...area,
            maturityLevel: targetMaturity,
            maturityScore: targetScore,
          };
        });
      });

      newData.improvementAreas = updatedAreas;
    }

    // If we have business value metrics, update their currentRating to match dimensions
    if (businessValueMetrics) {
      const updatedBusinessValueMetrics = { ...businessValueMetrics };
      const dimensionNameToStarsMap = {};

      // Create a map of dimension names to star ratings
      dimensions.forEach(dim => {
        dimensionNameToStarsMap[dim.name] = dim.stars;
      });

      // Update currentRating in business value metrics
      updatedBusinessValueMetrics.categories = updatedBusinessValueMetrics.categories.map(category => ({
        ...category,
        metrics: category.metrics.map(metric => ({
          ...metric,
          currentRating: dimensionNameToStarsMap[metric.name] || metric.currentRating,
        })),
      }));

      newData.businessValueMetrics = updatedBusinessValueMetrics;
    }

    return newData;
  };

  // Function to update both dimension and business value metric when star rating changes
  const handleStarChange = (dimensionName, stars) => {
    // Find and update the dimension
    const newDimensions = [...data.dimensions];
    const dimensionIndex = newDimensions.findIndex(d => d.name === dimensionName);

    if (dimensionIndex === -1) return;

    const changedDimension = newDimensions[dimensionIndex];
    changedDimension.stars = stars;
    changedDimension.scale = getScaleFromStars(stars);

    // Update dimensions first
    let updatedData = {
      ...data,
      dimensions: newDimensions,
    };

    // Update the business value metrics
    if (data.businessValueMetrics) {
      const updatedBusinessValueMetrics = { ...data.businessValueMetrics };

      // Find and update the metric in the business value metrics
      updatedBusinessValueMetrics.categories = updatedBusinessValueMetrics.categories.map(category => ({
        ...category,
        metrics: category.metrics.map(metric => {
          if (metric.name === dimensionName) {
            return {
              ...metric,
              currentRating: stars,
            };
          }
          return metric;
        }),
      }));

      updatedData.businessValueMetrics = updatedBusinessValueMetrics;
    }

    // Now adjust related improvement areas based on the changed dimension
    const newImprovementAreas = adjustAreaMaturity(data.relationships, data.improvementAreas, changedDimension);

    updatedData = {
      ...updatedData,
      improvementAreas: newImprovementAreas,
    };

    // Check and enforce boundary conditions
    updatedData = enforceGlobalBoundaryConditions(updatedData);

    // Update with all changes
    setData(updatedData);

    // Reset highlighted areas after star change
    setUi({
      ...ui,
      highlightedDimension: null,
      highlightedAreas: [],
      relationshipWeights: {},
      animatingCircle: 'all',
    });

    // Always automatically re-optimize the layout, not just in debug mode
    // Wait for the data update to complete
    setTimeout(() => {
      // Apply optimization in silent mode (no alerts or status changes)
      applyOptimizedLayout(true);
    }, 100);

    // Apply animation
    setTimeout(() => setUi(prevUi => ({ ...prevUi, animatingCircle: null })), 500);
  };

  const handleMaturityChange = (areaName, newLevel) => {
    // Find the area in calculatedAreas
    let foundArea = null;
    let foundQuadrant = null;

    Object.entries(calculatedAreas).forEach(([quadrant, areas]) => {
      const area = areas.find(a => a.name === areaName);
      if (area) {
        foundArea = area;
        foundQuadrant = quadrant;
      }
    });

    if (!foundArea || !foundQuadrant) return;

    // Update the area's maturity level and score in the original data structure
    const areas = [...data.improvementAreas[foundQuadrant]];
    const areaIndex = areas.findIndex(a => a.name === areaName);

    if (areaIndex === -1) return;

    // Convert maturity level to score
    const maturityScore = getMaturityLevelNumber(newLevel);

    // Update the maturity level and score
    areas[areaIndex] = {
      ...areas[areaIndex],
      maturityLevel: newLevel,
      maturityScore: maturityScore,
    };

    // Update improvement areas first
    const updatedData = {
      ...data,
      improvementAreas: {
        ...data.improvementAreas,
        [foundQuadrant]: areas,
      },
    };

    // Use the hook to recalculate star ratings for the affected area
    const finalData = recalculateStarRatingsForArea(updatedData, areaName);

    // Update with all changes
    setData(finalData);

    // Apply animation
    setUi({ ...ui, animatingCircle: 'all' });
    setTimeout(() => setUi({ ...ui, animatingCircle: null }), 500);
  };

  // Function to adjust area maturity based on dimension star change
  const adjustAreaMaturity = (relationships, areas, changedDimension) => {
    const starsToMaturityMap = {
      1: 'Weak',
      2: 'Weak',
      3: 'Emerging',
      4: 'Established',
      5: 'Peak',
    };

    // Find all areas affected by this dimension
    const relatedAreas = relationships
      .filter(rel => rel.dimension === changedDimension.name)
      .map(rel => ({
        areaName: rel.area,
        weight: rel.weight,
      }));

    if (relatedAreas.length === 0) {
      return areas; // No change
    }

    // Get the target maturity from stars
    const targetMaturity = starsToMaturityMap[changedDimension.stars];
    const targetScore =
      targetMaturity === 'Weak' ? 1 : targetMaturity === 'Emerging' ? 2 : targetMaturity === 'Established' ? 3 : 4;

    // Create a copy of all areas
    const newAreas = { ...areas };

    // Find an area across all quadrants
    const findAreaInQuadrants = areaName => {
      for (const [quadrant, areaList] of Object.entries(areas)) {
        const areaIndex = (areaList as ImprovementArea[]).findIndex((a: ImprovementArea) => a.name === areaName);
        if (areaIndex >= 0) {
          return { quadrant, areaIndex, area: areaList[areaIndex] };
        }
      }
      return null;
    };

    // Adjust each affected area
    relatedAreas.forEach(relArea => {
      const areaInfo = findAreaInQuadrants(relArea.areaName);

      if (areaInfo) {
        const { quadrant, areaIndex, area } = areaInfo;

        // Skip areas with null maturity levels - they should not be affected by star changes
        if (area.maturityLevel === null || area.maturityScore === null) {
          return;
        }

        // Current maturity
        const currentScore = area.maturityScore || 1;

        // Weight determines influence
        const weightInfluence = relArea.weight * 10; // Scale weight for impact

        // Calculate weighted maturity score
        let newScore = Math.round((currentScore + targetScore * weightInfluence) / (1 + weightInfluence));

        // Ensure score is within 1-4 range
        newScore = Math.max(1, Math.min(4, newScore));

        // Map score back to level
        const newLevel =
          newScore === 1 ? 'Weak' : newScore === 2 ? 'Emerging' : newScore === 3 ? 'Established' : 'Peak';

        // Update area in the correct quadrant
        newAreas[quadrant] = [...newAreas[quadrant]];
        newAreas[quadrant][areaIndex] = {
          ...newAreas[quadrant][areaIndex],
          maturityLevel: newLevel,
          maturityScore: newScore,
        };
      }
    });

    return newAreas;
  };

  // Group dimensions by category (from business value metrics)
  const getDimensionsByCategory = (): Record<string, Dimension[]> => {
    if (!data.businessValueMetrics) {
      // If no business value metrics, just return all dimensions
      return { 'Business Value Dimensions': data.dimensions };
    }

    const dimensionsByCategory = {};

    data.businessValueMetrics.categories.forEach(category => {
      dimensionsByCategory[category.name] = data.dimensions.filter(dim =>
        category.metrics.some(metric => metric.name === dim.name),
      );
    });

    return dimensionsByCategory;
  };

  useEffect(() => {
    // Just keep empty useEffect if needed elsewhere
  }, []);

  // Calculate top opportunities and update state
  useEffect(() => {
    setTopOpportunities(calculateTopOpportunities());
  }, [calculatedAreas]); // Re-calculate when calculatedAreas changes

  // Function to predict if a circle might cross the container boundary
  const predictBoundaryCrossing = () => {
    if (!calculatedAreas || Object.keys(calculatedAreas).length === 0) {
      return { crossingItems: [], quadrantCounts: {} };
    }

    const crossingItems = [];
    const quadrantCounts = {};

    // Estimate the radius of the container (45vh)
    const containerRadius = 45; // 90vh / 2 = 45vh

    // Estimate quadrant dimensions (container is split into 4 quadrants)
    const quadrantWidth = 40; // Reduced size for better spacing from outer circle
    const quadrantHeight = 40;

    // Process each quadrant
    Object.keys(calculatedAreas).forEach((quadrant, quadrantIndex) => {
      quadrantCounts[quadrant] = 0;

      const isLeft = quadrantIndex % 2 === 0;
      const isTop = quadrantIndex < 2;

      // Average size of circles in this quadrant to help estimate density
      const avgCircleSize =
        calculatedAreas[quadrant].reduce((sum, area) => sum + getCircleSize(area.calculatedImpact || 0), 0) /
        Math.max(calculatedAreas[quadrant].length, 1);

      // Check each area in the quadrant
      calculatedAreas[quadrant].forEach(area => {
        // Get circle size
        const circleSize = getCircleSize(area.calculatedImpact || 0);
        const circleRadius = circleSize / 2;

        // Estimate position based on quadrant and circle count
        let distanceFromCenter = 0;
        let boundaryIssue = null;

        // --- MAIN CONTAINER BOUNDARY CHECK ---
        // If the circle is large (over 80px) or the quadrant is crowded,
        // it's more likely to cross the main circular boundary
        if (circleSize > 80 || calculatedAreas[quadrant].length > 5) {
          // Calculate maximum safe distance from center where circle won't cross boundary
          const maxSafeDistance = containerRadius - circleRadius;

          // Estimate if this circle might be positioned near edge
          // Consider density - more items means higher chance of being pushed outward
          const itemCount = calculatedAreas[quadrant].length;
          const itemDensity = itemCount / (quadrantWidth * quadrantHeight);

          // Large impact and high density increase likelihood of boundary issues
          const isLikelyNearOuterEdge =
            (itemCount > 4 && area.calculatedImpact > 80) || (itemDensity > 0.01 && circleSize > avgCircleSize);

          if (isLikelyNearOuterEdge) {
            // Estimate this item could cross the main circular boundary
            distanceFromCenter = containerRadius - circleRadius + 5; // 5px past safe distance
            boundaryIssue = 'main';
          }
        }

        // --- QUADRANT BOUNDARY CHECK ---
        // Check if the circle might cross quadrant borders (center lines)
        // This is more likely for circles with larger sizes positioned in densely populated quadrants
        // and particularly likely for circles close to the center where quadrants meet

        // Estimate circle position within quadrant (as % from center toward edge)
        // Larger, higher-impact items are more likely to be positioned closer to quadrant center
        const positionFactor = 1 - (area.calculatedImpact / 100) * 0.5;

        // Calculate potential distance from quadrant borders
        // For quadrants on left side, check distance from right border (center vertical line)
        // For quadrants on right side, check distance from left border (center vertical line)
        // Similar logic for top/bottom

        // Horizontal check (distance from vertical center line)
        const distanceFromVerticalBorder = quadrantWidth * positionFactor * 0.8;
        const horizontalBorderIssue =
          (isLeft && distanceFromVerticalBorder + circleRadius > quadrantWidth) ||
          (!isLeft && distanceFromVerticalBorder < circleRadius);

        // Vertical check (distance from horizontal center line)
        const distanceFromHorizontalBorder = quadrantHeight * positionFactor * 0.8;
        const verticalBorderIssue =
          (isTop && distanceFromHorizontalBorder + circleRadius > quadrantHeight) ||
          (!isTop && distanceFromHorizontalBorder < circleRadius);

        // Adjust for the padding that's applied to quadrants
        const paddingBuffer = 40; // Updated to match the 40px quadrant padding
        const horizontalBorderWithPadding =
          horizontalBorderIssue && Math.abs(distanceFromVerticalBorder - circleRadius) < paddingBuffer;
        const verticalBorderWithPadding =
          verticalBorderIssue && Math.abs(distanceFromHorizontalBorder - circleRadius) < paddingBuffer;

        if (horizontalBorderWithPadding) {
          boundaryIssue = boundaryIssue || 'verticalBorder';
        }

        if (verticalBorderWithPadding) {
          boundaryIssue = boundaryIssue || 'horizontalBorder';
        }

        // Highly likely to cross center if very close to both borders (cross into another quadrant)
        const likelyCrossingCenter = horizontalBorderWithPadding && verticalBorderWithPadding;
        if (likelyCrossingCenter) {
          boundaryIssue = 'centerCross';
        }

        // Add to crossing items if any boundary issue is detected
        if (boundaryIssue) {
          crossingItems.push({
            name: area.name,
            quadrant,
            quadrantIndex,
            size: circleSize,
            estimatedOverlap: 5,
            boundaryIssue,
            isLeft,
            isTop,
          });

          quadrantCounts[quadrant] = (quadrantCounts[quadrant] || 0) + 1;
        }
      });
    });

    return { crossingItems, quadrantCounts };
  };

  // Function to check if two circles are colliding
  const checkCircleCollision = (circle1, circle2, padding = 5) => {
    // Get the center points and radii
    const x1 = circle1.x;
    const y1 = circle1.y;
    const r1 = circle1.radius + padding;

    const x2 = circle2.x;
    const y2 = circle2.y;
    const r2 = circle2.radius + padding;

    // Calculate the distance between the centers
    const dx = x2 - x1;
    const dy = y2 - y1;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Check if the distance is less than the sum of the radii
    return distance < r1 + r2;
  };

  // Function to generate a grid of positions for placing circles
  const generateGridPositions = (quadrantWidth, quadrantHeight, isLeft, isTop, circleCount) => {
    // Estimate how many rows and columns we need based on a simplified grid
    const estimatedCellSize = Math.sqrt((quadrantWidth * quadrantHeight) / circleCount);
    const cols = Math.ceil(quadrantWidth / estimatedCellSize);
    const rows = Math.ceil(quadrantHeight / estimatedCellSize);

    const positions = [];

    // Create positions starting from center (for better arrangement)
    const centerCol = isLeft ? cols - 1 : 0;
    const centerRow = isTop ? rows - 1 : 0;

    // Create a grid of positions spiraling outward from the center
    for (let radius = 0; radius < Math.max(cols, rows); radius++) {
      // Top row of this radius
      for (let i = -radius; i <= radius; i++) {
        const col = centerCol + i;
        const row = centerRow - radius;
        if (col >= 0 && col < cols && row >= 0 && row < rows) {
          positions.push({
            x: (col + 0.5) * (quadrantWidth / cols) * (isLeft ? 1 : -1),
            y: (row + 0.5) * (quadrantHeight / rows) * (isTop ? 1 : -1),
            distance: Math.abs(i) + radius, // Used for sorting
          });
        }
      }

      // Right column of this radius
      for (let i = -radius + 1; i <= radius; i++) {
        const col = centerCol + radius;
        const row = centerRow + i;
        if (col >= 0 && col < cols && row >= 0 && row < rows) {
          positions.push({
            x: (col + 0.5) * (quadrantWidth / cols) * (isLeft ? 1 : -1),
            y: (row + 0.5) * (quadrantHeight / rows) * (isTop ? 1 : -1),
            distance: radius + Math.abs(i),
          });
        }
      }

      // Bottom row of this radius
      for (let i = radius - 1; i >= -radius; i--) {
        const col = centerCol + i;
        const row = centerRow + radius;
        if (col >= 0 && col < cols && row >= 0 && row < rows) {
          positions.push({
            x: (col + 0.5) * (quadrantWidth / cols) * (isLeft ? 1 : -1),
            y: (row + 0.5) * (quadrantHeight / rows) * (isTop ? 1 : -1),
            distance: radius + Math.abs(i),
          });
        }
      }

      // Left column of this radius
      for (let i = radius - 1; i >= -radius + 1; i--) {
        const col = centerCol - radius;
        const row = centerRow + i;
        if (col >= 0 && col < cols && row >= 0 && row < rows) {
          positions.push({
            x: (col + 0.5) * (quadrantWidth / cols) * (isLeft ? 1 : -1),
            y: (row + 0.5) * (quadrantHeight / rows) * (isTop ? 1 : -1),
            distance: radius + Math.abs(i),
          });
        }
      }
    }

    // Sort positions by distance from center
    positions.sort((a, b) => a.distance - b.distance);

    // Sign the positions correctly based on quadrant
    return positions.map(pos => ({
      ...pos,
      x: isLeft ? Math.abs(pos.x) : -Math.abs(pos.x),
      y: isTop ? Math.abs(pos.y) : -Math.abs(pos.y),
    }));
  };

  // Function to reorder areas for better boundary management
  const createOptimizedLayout = () => {
    // Clone the current calculated areas to avoid direct mutation
    let optimizedAreas = JSON.parse(JSON.stringify(calculatedAreas));

    // Get potential boundary crossing items for current layout
    let { crossingItems, quadrantCounts } = predictBoundaryCrossing();

    if (crossingItems.length === 0) {
      console.log('No boundary issues detected, checking for collisions...');
      // Even if there are no boundary issues, we should still check for collisions
    }

    // Track our optimization attempts and results
    const maxAttempts = 20;
    const optimizationResults = [];

    console.log(`Starting optimization: Found ${crossingItems.length} potential boundary issues`);

    // Make multiple attempts to optimize the layout
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`Optimization attempt ${attempt}/${maxAttempts} - ${crossingItems.length} issues remaining`);

      // Calculate the aggressiveness factor that increases with each attempt
      // Three tiers of aggressiveness:
      // - First 5 attempts: Gentle adjustments (1.5 to 3.5)
      // - Attempts 6-10: Moderate adjustments (4.3 to 7.5)
      // - Attempts 11-20: Aggressive adjustments (8.5 to 16.5)
      let aggressiveness;
      if (attempt <= 5) {
        // First tier: Gentle (1.5, 2.0, 2.5, 3.0, 3.5)
        aggressiveness = 1 + attempt * 0.5;
      } else if (attempt <= 10) {
        // Second tier: Moderate (4.3, 5.1, 5.9, 6.7, 7.5)
        aggressiveness = 3.5 + (attempt - 5) * 0.8;
      } else {
        // Third tier: Aggressive (8.5, 9.5, ... 16.5)
        aggressiveness = 7.5 + (attempt - 10) * 0.9;
      }

      // Process each quadrant
      // eslint-disable-next-line no-loop-func
      Object.keys(optimizedAreas).forEach((quadrant, quadrantIndex) => {
        const areas = optimizedAreas[quadrant];
        const isLeft = quadrantIndex % 2 === 0;
        const isTop = quadrantIndex < 2;

        // Step 1: Sort areas by size (largest first)
        areas.sort((a, b) => {
          const sizeA = getCircleSize(a.calculatedImpact || 0);
          const sizeB = getCircleSize(b.calculatedImpact || 0);
          return sizeB - sizeA; // Largest first
        });

        // Step 1: Sort or shuffle areas based on the current attempt
        if (attempt <= 5) {
          // First few attempts - sort by size (largest first) for a sensible initial arrangement
          areas.sort((a, b) => {
            const sizeA = getCircleSize(a.calculatedImpact || 0);
            const sizeB = getCircleSize(b.calculatedImpact || 0);
            return sizeB - sizeA; // Largest first
          });
        } else if (attempt <= 10) {
          // Middle attempts - use a weighted random sort that still prefers larger items first
          // but introduces some randomness
          areas.sort((a, b) => {
            const sizeA = getCircleSize(a.calculatedImpact || 0);
            const sizeB = getCircleSize(b.calculatedImpact || 0);
            const randomFactor = Math.random() * 0.4 - 0.2; // Random value between -0.2 and 0.2
            return sizeB - sizeA + randomFactor * 40; // Size diff with random influence
          });
        } else if (attempt <= 15) {
          // Later attempts - sort by maturity score to try a completely different arrangement
          // This groups similar maturity levels together, which might create a more logical layout
          areas.sort((a, b) => {
            const maturityA = a.maturityScore || 1;
            const maturityB = b.maturityScore || 1;
            return maturityB - maturityA; // Higher maturity first
          });

          // Also apply some subsection shuffling - split the array and shuffle within segments
          // This maintains some order while introducing controlled randomness
          const splitPoint = Math.floor(areas.length / 2);
          const firstHalf = areas.slice(0, splitPoint);
          const secondHalf = areas.slice(splitPoint);

          // Fisher-Yates shuffle for each half
          for (let i = firstHalf.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [firstHalf[i], firstHalf[j]] = [firstHalf[j], firstHalf[i]];
          }

          for (let i = secondHalf.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [secondHalf[i], secondHalf[j]] = [secondHalf[j], secondHalf[i]];
          }

          // Recombine
          areas.splice(0, areas.length, ...firstHalf, ...secondHalf);
        } else {
          // Final attempts - completely randomize order to explore radically different layouts
          // Using Fisher-Yates shuffle algorithm
          for (let i = areas.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [areas[i], areas[j]] = [areas[j], areas[i]];
          }
        }

        // Step 2: Generate grid positions for this quadrant
        const quadrantWidth = 40; // Reduced from half (90vh / 2)
        const quadrantHeight = 40; // Reduced from half (90vh / 2)

        // Generate grid positions for this quadrant
        const gridPositions = generateGridPositions(quadrantWidth, quadrantHeight, isLeft, isTop, areas.length + 5);

        // Create a map to track placed circles for collision detection
        const placedCircles = [];

        // First pass: handle boundary crossing items
        areas.forEach((area, index) => {
          const circleSize = getCircleSize(area.calculatedImpact || 0);
          const circleRadius = circleSize / 2;

          // Find if this area is in the crossing items list
          const matchingCrossingItem = crossingItems.find(
            item => item.name === area.name && item.quadrant === quadrant,
          );

          // If this is a problematic area, adjust its position
          if (matchingCrossingItem) {
            // Initial position adjustments for boundary items
            const baseOffset = 10 * aggressiveness;

            // Different adjustments based on boundary issue type
            if (!area.optimizedPosition) {
              // Initialize optimization position object
              let offsetX = 0;
              let offsetY = 0;

              // Apply appropriate offset based on the specific boundary issue
              switch (matchingCrossingItem.boundaryIssue) {
                case 'main': // Main circular container boundary
                  // Move towards center from outer edge
                  offsetX = isLeft ? baseOffset : -baseOffset;
                  offsetY = isTop ? baseOffset : -baseOffset;
                  break;

                case 'verticalBorder': // Crossing vertical center line
                  // Adjust horizontally away from center
                  offsetX = isLeft ? -baseOffset : baseOffset;
                  break;

                case 'horizontalBorder': // Crossing horizontal center line
                  // Adjust vertically away from center
                  offsetY = isTop ? -baseOffset : baseOffset;
                  break;

                case 'centerCross': // Crossing into another quadrant at center
                  // Move away from center intersection
                  offsetX = isLeft ? -baseOffset * 1.5 : baseOffset * 1.5;
                  offsetY = isTop ? -baseOffset * 1.5 : baseOffset * 1.5;
                  break;

                default: // Default behavior if boundary issue type not specified
                  offsetX = isLeft ? baseOffset : -baseOffset;
                  offsetY = isTop ? baseOffset : -baseOffset;
              }

              area.optimizedPosition = {
                offsetX,
                offsetY,
                priority: attempt,
              };
            } else {
              // Make adjustments more aggressive with each attempt
              // Determine how to adjust based on boundary issue type
              const boundaryType = matchingCrossingItem.boundaryIssue;

              if (boundaryType === 'main') {
                // Main boundary issue - move further toward center
                area.optimizedPosition.offsetX = isLeft
                  ? Math.max(area.optimizedPosition.offsetX, baseOffset)
                  : Math.min(area.optimizedPosition.offsetX, -baseOffset);
                area.optimizedPosition.offsetY = isTop
                  ? Math.max(area.optimizedPosition.offsetY, baseOffset)
                  : Math.min(area.optimizedPosition.offsetY, -baseOffset);
              } else if (boundaryType === 'verticalBorder') {
                // Vertical boundary issue - move further horizontally away from center
                area.optimizedPosition.offsetX = isLeft
                  ? Math.min(area.optimizedPosition.offsetX, -baseOffset)
                  : Math.max(area.optimizedPosition.offsetX, baseOffset);
              } else if (boundaryType === 'horizontalBorder') {
                // Horizontal boundary issue - move further vertically away from center
                area.optimizedPosition.offsetY = isTop
                  ? Math.min(area.optimizedPosition.offsetY, -baseOffset)
                  : Math.max(area.optimizedPosition.offsetY, baseOffset);
              } else if (boundaryType === 'centerCross') {
                // Center crossing issue - move further away from center intersection
                area.optimizedPosition.offsetX = isLeft
                  ? Math.min(area.optimizedPosition.offsetX, -baseOffset * 1.5)
                  : Math.max(area.optimizedPosition.offsetX, baseOffset * 1.5);
                area.optimizedPosition.offsetY = isTop
                  ? Math.min(area.optimizedPosition.offsetY, -baseOffset * 1.5)
                  : Math.max(area.optimizedPosition.offsetY, baseOffset * 1.5);
              }

              // Update priority
              area.optimizedPosition.priority = attempt;
            }

            // For the last attempt, consider shrinking problematic circles slightly
            if (attempt === maxAttempts && circleSize > 80) {
              area.sizeAdjustment = 0.9; // Reduce size by 10% as last resort
            }

            // Apply progressive size adjustments for problematic circles in later attempts
            if (attempt > 5) {
              // Calculate adjustment factor based on attempt number and circle size
              if (circleSize > 80) {
                // Progressive size reduction based on attempt number (6-10)
                // Start with 5% reduction at attempt 6, increasing to 15% by attempt 10
                // 0.95, 0.93, 0.90, 0.87, 0.85
                const reductionFactor = 0.95 - (attempt - 6) * 0.025;

                // Apply more aggressive adjustments for specific boundary types
                if (matchingCrossingItem.boundaryIssue === 'centerCross') {
                  // Center crossing items need more aggressive size reduction
                  area.sizeAdjustment = reductionFactor - 0.05;
                } else if (matchingCrossingItem.boundaryIssue === 'main') {
                  // Main boundary items need significant reduction too
                  area.sizeAdjustment = reductionFactor - 0.02;
                } else {
                  // Other boundary issues get standard progressive reduction
                  area.sizeAdjustment = reductionFactor;
                }
              }
              // Medium-sized circles get less aggressive adjustments (from attempts 8-10)
              else if (circleSize > 60 && attempt > 7) {
                area.sizeAdjustment = 0.92 - (attempt - 8) * 0.02; // 0.92, 0.90, 0.88
              }
            }

            // Add this circle to the placed circles list
            placedCircles.push({
              name: area.name,
              x: area.optimizedPosition.offsetX,
              y: area.optimizedPosition.offsetY,
              radius: circleRadius * (area.sizeAdjustment || 1),
              index: index,
            });

            quadrantCounts[quadrant] = (quadrantCounts[quadrant] || 0) + 1;
          }
        });

        // Second pass: position remaining items to avoid collisions
        areas.forEach((area, index) => {
          // Skip items that already have positions from boundary crossing
          if (crossingItems.some(item => item.name === area.name && item.quadrant === quadrant)) {
            return;
          }

          const circleSize = getCircleSize(area.calculatedImpact || 0);
          const circleRadius = circleSize / 2;

          // Try to find a position without collisions
          let bestPosition = null;
          let bestCollisionCount = Infinity;

          // Try each grid position to find the best one
          for (let i = 0; i < gridPositions.length; i++) {
            const pos = gridPositions[i];

            // Create a test circle at this position
            const testCircle = {
              x: pos.x,
              y: pos.y,
              radius: circleRadius * (area.sizeAdjustment || 1),
            };

            // Count collisions with already placed circles
            let collisionCount = 0;
            let hasCollision = false;

            for (const placedCircle of placedCircles) {
              if (checkCircleCollision(testCircle, placedCircle)) {
                collisionCount++;

                // For initial passes, any collision is bad
                if (attempt < 3) {
                  hasCollision = true;
                  break;
                }
              }
            }

            // Check boundary collision
            const distanceFromCenter = Math.sqrt(pos.x * pos.x + pos.y * pos.y);
            const isBoundaryCrossing = distanceFromCenter + circleRadius > 45;

            if (isBoundaryCrossing) {
              collisionCount += 10; // Heavy penalty for boundary crossings
            }

            // For early attempts, reject any position with collisions
            if (attempt < 3 && hasCollision) {
              continue;
            }

            // Update best position if this one has fewer collisions
            if (collisionCount < bestCollisionCount) {
              bestPosition = pos;
              bestCollisionCount = collisionCount;

              // If no collisions, we've found an ideal position
              if (collisionCount === 0) {
                break;
              }
            }
          }

          // If we found a decent position, use it
          if (bestPosition) {
            area.optimizedPosition = {
              offsetX: bestPosition.x,
              offsetY: bestPosition.y,
              gridPosition: true, // Flag that this is a grid-placed item
              priority: attempt,
            };

            // Add this circle to placed circles
            placedCircles.push({
              name: area.name,
              x: bestPosition.x,
              y: bestPosition.y,
              radius: circleRadius * (area.sizeAdjustment || 1),
              index: index,
            });
          }

          // If we're in later attempts and still have collisions,
          // consider slightly reducing the size
          if (attempt >= 3 && bestCollisionCount > 0 && circleSize > 60) {
            area.sizeAdjustment = 0.9; // Reduce size by 10%
          }
        });
      });

      // Re-evaluate boundary crossing with our new optimized layout
      const newEvaluation = predictBoundaryCrossingForLayout(optimizedAreas);
      crossingItems = newEvaluation.crossingItems;
      quadrantCounts = newEvaluation.quadrantCounts;

      optimizationResults.push({
        attempt,
        crossingItemsCount: crossingItems.length,
        quadrantCounts: { ...quadrantCounts },
      });

      // If we've fixed all boundary issues and we're on the last attempt,
      // perform a final collision check
      if (crossingItems.length === 0 && attempt === maxAttempts) {
        // Count how many circles have collisions after all our work
        let totalCollisions = 0;

        Object.keys(optimizedAreas).forEach((quadrant, quadrantIndex) => {
          const areas = optimizedAreas[quadrant];

          // Create a list of all circles in this quadrant
          const circles = areas.map((area, index) => {
            const circleSize = getCircleSize(area.calculatedImpact || 0) * (area.sizeAdjustment || 1);
            const circleRadius = circleSize / 2;

            let x = 0;
            let y = 0;

            if (area.optimizedPosition) {
              x = area.optimizedPosition.offsetX;
              y = area.optimizedPosition.offsetY;
            }

            return {
              name: area.name,
              x,
              y,
              radius: circleRadius,
              index,
            };
          });

          // Check each circle against every other circle
          for (let i = 0; i < circles.length; i++) {
            for (let j = i + 1; j < circles.length; j++) {
              if (checkCircleCollision(circles[i], circles[j])) {
                totalCollisions++;
              }
            }
          }
        });

        console.log(`Final collision check: ${totalCollisions} collisions remaining`);
      }

      // If we've fixed all boundary issues, we can stop
      if (crossingItems.length === 0 && attempt >= 3) {
        console.log(`Optimization successful after ${attempt} attempts!`);
        break;
      }
    }

    // Log the optimization results
    console.log('Optimization results:', optimizationResults);

    // Final evaluation result
    const finalCrossingCount = crossingItems.length;
    if (finalCrossingCount > 0) {
      console.log(
        `After ${maxAttempts} optimization attempts, ${finalCrossingCount} potential boundary issues remain.`,
      );
    } else {
      console.log(`Optimization successful! All boundary issues resolved.`);
    }

    return optimizedAreas;
  };

  // Modified version of predictBoundaryCrossing that works on a provided layout
  const predictBoundaryCrossingForLayout = areasLayout => {
    if (!areasLayout || Object.keys(areasLayout).length === 0) {
      return { crossingItems: [], quadrantCounts: {} };
    }

    const crossingItems = [];
    const quadrantCounts = {};

    // Estimate the radius of the container (45vh)
    const containerRadius = 45; // 90vh / 2 = 45vh

    // Estimate quadrant dimensions (container is split into 4 quadrants)
    const quadrantWidth = 40; // Reduced size for better spacing from outer circle
    const quadrantHeight = 40;

    // Process each quadrant
    Object.keys(areasLayout).forEach((quadrant, quadrantIndex) => {
      quadrantCounts[quadrant] = 0;

      // Check each area in the quadrant
      areasLayout[quadrant].forEach(area => {
        const isLeft = quadrantIndex % 2 === 0;
        const isTop = quadrantIndex < 2;

        // Get circle size (accounting for any size adjustments from optimization)
        let circleSize = getCircleSize(area.calculatedImpact || 0);
        if (area.sizeAdjustment) {
          circleSize *= area.sizeAdjustment;
        }
        const circleRadius = circleSize / 2;

        // Factor in any position adjustments
        let distanceFromEdge = 0;
        if (area.optimizedPosition) {
          // If we've moved the circle inward, it's less likely to cross the boundary
          const inwardOffset =
            Math.abs(area.optimizedPosition.offsetX || 0) + Math.abs(area.optimizedPosition.offsetY || 0);
          distanceFromEdge += inwardOffset;
        }

        // If the circle is large and still might cross the boundary
        if (circleSize > 80 && distanceFromEdge < 15) {
          const itemCount = areasLayout[quadrant].length;
          const isLikelyNearEdge = itemCount > 4 && area.calculatedImpact > 75;

          if (isLikelyNearEdge) {
            crossingItems.push({
              name: area.name,
              quadrant,
              quadrantIndex,
              size: circleSize,
              estimatedOverlap: 5 - distanceFromEdge / 3, // Less overlap with more inward offset
              isLeft,
              isTop,
            });

            quadrantCounts[quadrant] = (quadrantCounts[quadrant] || 0) + 1;
          }
        }
      });
    });

    return { crossingItems, quadrantCounts };
  };

  // State for optimized layout
  const [optimizedLayout, setOptimizedLayout] = useState<CalculatedAreas | null>(null);

  // State for optimization status
  const [optimizationStatus, setOptimizationStatus] = useState({
    inProgress: false,
    attempts: 0,
    remainingIssues: 0,
  });

  // Toggle debug mode to check for boundary crossing
  const toggleDebugMode = () => {
    if (!ui.debugMode) {
      // Analyze potential crossing
      const { crossingItems, quadrantCounts } = predictBoundaryCrossing();

      console.log('Potential boundary crossing items:', crossingItems);
      console.log('Items per quadrant:', quadrantCounts);

      // Alert the user with findings
      if (crossingItems.length > 0) {
        // Group items by issue type
        const mainBoundaryItems = crossingItems.filter(item => item.boundaryIssue === 'main');
        const verticalBorderItems = crossingItems.filter(item => item.boundaryIssue === 'verticalBorder');
        const horizontalBorderItems = crossingItems.filter(item => item.boundaryIssue === 'horizontalBorder');
        const centerCrossItems = crossingItems.filter(item => item.boundaryIssue === 'centerCross');

        alert(
          `Found ${crossingItems.length} items that might have boundary issues.\n\n` +
            `Affected quadrants: ${Object.entries(quadrantCounts)
              .filter(([_, count]) => (count as number) > 0)
              .map(([q, count]) => `${q} (${count})`)
              .join(', ')}\n\n` +
            // Group by boundary issue type
            `Issues by type:\n` +
            `- Main circular boundary: ${mainBoundaryItems.length} items\n` +
            `- Crossing center vertical line: ${verticalBorderItems.length} items\n` +
            `- Crossing center horizontal line: ${horizontalBorderItems.length} items\n` +
            `- Crossing into another quadrant: ${centerCrossItems.length} items\n\n` +
            `Items: ${crossingItems.map(item => `${item.name} (${item.boundaryIssue || 'unknown'})`).join(', ')}`,
        );
      } else {
        alert('No items detected that would likely cross the boundary.');
      }
    }

    setUi(prevUi => ({ ...prevUi, debugMode: !prevUi.debugMode }));

    // Clear optimized layout when exiting debug mode
    if (ui.debugMode) {
      setOptimizedLayout(null);
    }
  };

  // Function to apply optimized layout
  const applyOptimizedLayout = (silent = false) => {
    // Show optimization in progress (unless in silent mode)
    if (!silent) {
      setOptimizationStatus({
        inProgress: true,
        attempts: 0,
        remainingIssues: 0,
      });
    }

    // Use setTimeout to allow the UI to update before starting optimization
    setTimeout(() => {
      // Run the optimization algorithm
      const optimized = createOptimizedLayout();

      // Get final crossing count
      const { crossingItems } = predictBoundaryCrossingForLayout(optimized);

      // Count remaining collisions
      let collisionCount = 0;
      Object.keys(optimized).forEach(quadrant => {
        const areas = optimized[quadrant];
        const circles = areas.map(area => {
          const size = getCircleSize(area.calculatedImpact || 0) * (area.sizeAdjustment || 1);
          return {
            name: area.name,
            x: area.optimizedPosition?.offsetX || 0,
            y: area.optimizedPosition?.offsetY || 0,
            radius: size / 2,
          };
        });

        // Check each circle against every other circle
        for (let i = 0; i < circles.length; i++) {
          for (let j = i + 1; j < circles.length; j++) {
            if (checkCircleCollision(circles[i], circles[j])) {
              collisionCount++;
            }
          }
        }
      });

      // Update state with optimized layout and status
      setOptimizedLayout(optimized);

      // Only update status if not in silent mode
      if (!silent) {
        setOptimizationStatus({
          inProgress: false,
          attempts: 20, // Maximum of 20 attempts now
          remainingIssues: crossingItems.length + collisionCount,
        });

        // Show alert with results
        if (crossingItems.length === 0 && collisionCount === 0) {
          alert('Successfully optimized the layout! All boundary crossings and collisions have been resolved.');
        } else if (crossingItems.length === 0) {
          alert(
            `Layout optimization completed. All boundary crossings resolved, but ${collisionCount} minor overlaps remain.`,
          );
        } else {
          // Group items by issue type for detailed report
          const mainBoundaryItems = crossingItems.filter(item => item.boundaryIssue === 'main');
          const verticalBorderItems = crossingItems.filter(item => item.boundaryIssue === 'verticalBorder');
          const horizontalBorderItems = crossingItems.filter(item => item.boundaryIssue === 'horizontalBorder');
          const centerCrossItems = crossingItems.filter(item => item.boundaryIssue === 'centerCross');

          alert(
            `Applied optimized layout with best possible arrangement.\n\n` +
              `Remaining boundary issues (${crossingItems.length} total):\n` +
              `- Main circular boundary: ${mainBoundaryItems.length} items\n` +
              `- Crossing center vertical line: ${verticalBorderItems.length} items\n` +
              `- Crossing center horizontal line: ${horizontalBorderItems.length} items\n` +
              `- Crossing into another quadrant: ${centerCrossItems.length} items\n\n` +
              `Remaining collisions: ${collisionCount}\n\n` +
              `Their impact has been minimized as much as possible.`,
          );
        }
      }
    }, 50);
  };

  // Function to restore original layout
  const restoreOriginalLayout = () => {
    setOptimizedLayout(null);
    alert('Restored original layout.');
  };

  // Handler for circle hover state
  const handleHoverChange = useCallback((areaName: string | null, hovering: boolean) => {
    // console.log("App received hover event:", areaName, hovering);

    if (!hovering) {
      // This is triggered when:
      // 1. Mouse leaves a circle
      // 2. Dialog is closed by clicking outside
      // console.log("Resetting hover state for:", areaName);
      setActiveDialog(null);
      setHoveredArea(null);
      return;
    }

    // For hover/dialog opening events
    setActiveDialog(areaName);
    setHoveredArea(areaName);
  }, []);

  // Debug log to check if hoveredArea state is updating
  useEffect(() => {
    console.log('Hovered area changed to:', hoveredArea);

    // Let's log all relationships related to this area
    if (hoveredArea && data && data.improvementAreas) {
      console.log('Looking for relationships for area:', hoveredArea);

      // Find all relationships for this area
      const areaRelationships = data.relationships.filter(rel => rel.area === hoveredArea);
      console.log('Found relationships:', areaRelationships);
    }
  }, [hoveredArea, data]);

  // Helper function to get the maturity level of the hovered area
  const getHoveredAreaMaturity = useCallback(() => {
    if (!hoveredArea || !data || !data.improvementAreas) return 'Established';

    // Find the hovered improvement area across all quadrants
    for (const [quadrant, areas] of Object.entries(data.improvementAreas)) {
      const area = areas.find(a => a.name === hoveredArea);
      if (area) {
        return area.maturityLevel || 'Established';
      }
    }

    return 'Established'; // Default if not found
  }, [hoveredArea, data]);

  // Helper function to get the quadrant of the hovered area
  const getHoveredAreaQuadrant = useCallback(() => {
    if (!hoveredArea || !data || !data.improvementAreas) return '';

    // Find the hovered improvement area across all quadrants
    for (const [quadrant, areas] of Object.entries(data.improvementAreas)) {
      const area = areas.find(a => a.name === hoveredArea);
      if (area) {
        return quadrant;
      }
    }

    return ''; // Default if not found
  }, [hoveredArea, data]);

  // Helper function to get the quadrant color for highlighting
  const getQuadrantColor = useCallback((quadrant: string) => {
    // Define quadrant colors - same as in D3BubbleChart component
    const quadrantColors = {
      Craftsmanship: '#4a9cde', // Blue
      Innovation: '#b55dd7', // Purple
      Collaboration: '#3eb489', // Green
      Delivery: '#e94f59', // Red
    };

    return quadrantColors[quadrant] || '#888888'; // Gray if quadrant not found
  }, []);

  const isMetricRelatedToHoveredArea = useCallback(
    (dimensionName: string) => {
      if (!hoveredArea || !data) return false;

      // Use direct relationship data instead of looking through improvement areas
      if (data.relationships) {
        // Find any relationship where this dimension is connected to the hovered area
        const isRelated = data.relationships.some(rel => rel.area === hoveredArea && rel.dimension === dimensionName);

        console.log(
          `Checking relationship: ${dimensionName} to ${hoveredArea} = ${isRelated ? 'RELATED' : 'not related'}`,
        );
        return isRelated;
      }

      return false;
    },
    [hoveredArea, data],
  );

  if (status.loading) return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
  if (status.error)
    return <div style={{ textAlign: 'center', color: 'red', padding: '40px' }}>Error: {status.error}</div>;

  const dimensionsByCategory = getDimensionsByCategory();

  // Handler for file upload
  const handleImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const json = JSON.parse(e.target?.result as string);
        // json is expected to be in the format of maturity-assessment-results.json
        // { "Testing": { category: ..., assessment: { maturity_level: ... } } }
        // We'll match area names (e.g., "Testing") and set their maturityLevel
        const newImprovementAreas = { ...data.improvementAreas };
        let foundAny = false;
        Object.entries(json).forEach(([areaName, areaData]: any) => {
          const maturityLevel = areaData?.assessment?.maturity_level;
          if (!maturityLevel) return;
          // Find the area in all quadrants
          for (const [quadrant, areas] of Object.entries(newImprovementAreas)) {
            const idx = (areas as ImprovementArea[]).findIndex(a => a.name === areaName);
            if (idx !== -1) {
              (newImprovementAreas[quadrant] as ImprovementArea[])[idx] = {
                ...areas[idx],
                maturityLevel,
              };
              foundAny = true;
            }
          }
        });
        if (!foundAny) {
          setImportError('No matching improvement areas found in the uploaded file.');
        } else {
          setData({ ...data, improvementAreas: newImprovementAreas });
          setShowImportModal(false);
          setImportError(null);
        }
      } catch (err) {
        setImportError('Invalid JSON file. Please check the format.');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div
      style={{
        padding: '24px',
        paddingTop: '88px', // Add padding for the fixed header (64px) plus some spacing
        borderRadius: '12px',
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '12px 32px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          height: '64px',
        }}
      >
        <img
          src="/logo.svg"
          alt="Engineering Culture Logo"
          style={{
            height: '48px',
            width: 'auto',
            filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        />
        <h1
          style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: 600,
            color: '#1f2937',
            letterSpacing: '-0.025em',
          }}
        >
          |&nbsp;&nbsp;&nbsp;Engineering Culture&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;Model
        </h1>
      </div>
      {/* Import Assessment Button */}
      <div
        style={{
          position: 'fixed',
          top: '22px',
          right: '24px',
          zIndex: 1000,
          display: 'flex',
          gap: '8px',
        }}
      >
        <button
          style={{
            background: ' #FF9F0A',
            color: '#1f2937',
            height: '44px',
            minWidth: '180px',
            border: 'none',
            borderRadius: '10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            fontSize: '18px',
            fontWeight: 600,
            fontFamily: "'Poppins', sans-serif",
            padding: '0 28px',
            cursor: 'pointer',
            transition: 'box-shadow 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseOver={e => {
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 16px rgba(2, 1, 0, 0.3)';
          }}
          onMouseOut={e => {
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
          }}
          onClick={() => setShowImportModal(true)}
        >
          Import Assessment
        </button>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.18)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={handleCloseImportModal}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 6px 32px rgba(0,0,0,0.18)',
              padding: '32px 40px',
              minWidth: '340px',
              maxWidth: '90vw',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '18px', color: '#1f2937' }}>
              Import Assessment Results
            </h2>
            <input
              type="file"
              accept="application/json"
              onChange={handleImportFileChange}
              style={{ marginBottom: '10px' }}
            />
            {importFileName && (
              <div style={{ fontSize: '14px', color: '#374151', marginBottom: '10px' }}>
                Selected file: <b>{importFileName}</b>
              </div>
            )}
            {importError && (
              <div style={{ color: '#dc2626', marginBottom: '8px', fontSize: '14px' }}>{importError}</div>
            )}
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <button
                style={{
                  background: '#FF9F0A',
                  color: '#1f2937',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 18px',
                  fontSize: '15px',
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 500,
                  cursor: 'pointer',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}
                onClick={handleImportSubmit}
                disabled={!importFile || !importFileContent}
              >
                Submit
              </button>
              <button
                style={{
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 18px',
                  fontSize: '15px',
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
                onClick={handleCloseImportModal}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Framework Context */}
      <div
        className="framework-context-container"
        style={{
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '24px',
          marginTop: '24px',
        }}
      >
        <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>Framework Context</h2>
        <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
          <p style={{ marginBottom: '8px' }}>
            This visualization helps bridge the gap between business value dimensions and technical improvement areas.
            It allows stakeholders to identify which improvement initiatives will deliver the most business value, while
            helping technical teams understand how their work directly contributes to business goals.
          </p>
          <p>
            Use this tool to identify high-impact, low-maturity areas for prioritization, track progress of improvement
            initiatives, and communicate the relationship between technical excellence and business outcomes across your
            organization.
          </p>
        </div>
      </div>

      {/* Two-column layout */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '24px',
          marginBottom: '24px',
        }}
      >
        {/* Left column - Business Value Dimensions (25% width) */}
        <div
          className="business-value-dimensions-container"
          style={{
            width: '25%',
            padding: '16px',
            borderRadius: '12px',
            height: 'auto',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.08)',
            background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 1))',
            border: '1px solid rgba(229, 231, 235, 0.8)',
          }}
        >
          <h2
            style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '16px',
              position: 'sticky',
              top: '0',
              padding: '6px 0',
              zIndex: '1',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(to right, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.8))',
              borderRadius: '8px',
              color: '#374151',
              textShadow: '0px 1px 1px rgba(255, 255, 255, 0.5)',
            }}
          >
            {'Business Value Metrics'}
          </h2>

          {/* Render dimensions by category */}
          {Object.entries(dimensionsByCategory).map(([category, dimensions]) => (
            <div key={category} style={{ marginBottom: '16px' }}>
              <h3
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: '#4b5563',
                  paddingBottom: '4px',
                  paddingTop: '4px',
                  paddingLeft: '10px',
                  paddingRight: '10px',
                  borderRadius: '8px',
                  background: 'linear-gradient(to right, rgba(229, 231, 235, 0.4), rgba(229, 231, 235, 0.1))',
                  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                  display: 'inline-block',
                }}
              >
                {category}
              </h3>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                }}
              >
                {(dimensions as Dimension[]).map((dim: Dimension) => (
                  <div
                    key={dim.name}
                    style={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb', // Always keep a neutral border color
                      padding: '8px 12px',
                      borderRadius: '12px',
                      marginBottom: '4px',
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    className={dim.description ? 'dimension-item-with-tooltip' : ''}
                    onMouseEnter={e => {
                      // Find all improvement areas related to this dimension
                      const relatedAreas = data.relationships
                        .filter(rel => rel.dimension === dim.name)
                        .map(rel => ({
                          area: rel.area,
                          weight: rel.weight,
                        }));

                      setUi({
                        ...ui,
                        highlightedDimension: dim.name,
                        highlightedAreas: relatedAreas.map(r => r.area),
                        relationshipWeights: relatedAreas.reduce((obj, rel) => {
                          obj[rel.area] = rel.weight;
                          return obj;
                        }, {}),
                      });

                      // Add hover effect
                      e.currentTarget.style.boxShadow = '0px 3px 6px rgba(0, 0, 0, 0.1)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={e => {
                      setUi({
                        ...ui,
                        highlightedDimension: null,
                        highlightedAreas: [],
                        relationshipWeights: {},
                      });

                      // Remove hover effect
                      e.currentTarget.style.boxShadow = '0px 2px 4px rgba(0, 0, 0, 0.05)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {/* Add a subtle inner decorative element similar to circles */}
                    <div
                      style={{
                        position: 'absolute',
                        top: '4px',
                        left: '4px',
                        right: '4px',
                        bottom: '4px',
                        borderRadius: '8px',
                        border: isMetricRelatedToHoveredArea(dim.name)
                          ? `1px solid ${getQuadrantColor(getHoveredAreaQuadrant())}`
                          : '1px solid rgba(0, 0, 0, 0.03)',
                        pointerEvents: 'none',
                      }}
                    ></div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: '1',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <h4
                          style={{
                            fontWeight: '500',
                            margin: 0,
                            cursor: 'default',
                            color: '#4b5563',
                          }}
                        >
                          {dim.name}
                        </h4>
                      </div>
                      <div style={{ display: 'flex', marginLeft: '8px' }}>
                        {[1, 2, 3, 4, 5].map(star => {
                          // Find the description for this star
                          const starDescription =
                            (dim.category &&
                              data.businessValueMetrics?.categories
                                .find(cat => cat.name === dim.category)
                                ?.metrics.find(m => m.name === dim.name)
                                ?.ratings.find(r => r.stars === star)?.description) ||
                            '';

                          // Format the tooltip text
                          const tooltipTitle = starDescription
                            ? `${star} ${star === 1 ? 'Star' : 'Stars'}: ${starDescription}`
                            : '';

                          return (
                            <button
                              key={star}
                              style={{
                                color: star <= dim.stars ? '#facc15' : '#d1d5db',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px',
                                padding: '3px',
                                outline: 'none',
                                transition: 'transform 0.2s ease, color 0.2s ease',
                                transform: star <= dim.stars ? 'scale(1.1)' : 'scale(1)',
                              }}
                              onClick={() => handleStarChange(dim.name, star)}
                              title={tooltipTitle}
                              onMouseEnter={e => {
                                e.currentTarget.style.transform = 'scale(1.2)';
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.transform = star <= dim.stars ? 'scale(1.1)' : 'scale(1)';
                              }}
                            >
                              ★
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Description that appears on hover */}
                    {dim.description && (
                      <div className="dimension-tooltip-content">
                        <p
                          style={{
                            margin: '8px 0 0 0',
                            fontSize: '12px',
                            lineHeight: '1.4',
                            color: '#666',
                          }}
                        >
                          {dim.description}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Right column - Improvement Areas (75% width) */}
        <div
          className="improvement-areas-container"
          style={{
            width: '75%',
            padding: '16px',
            borderRadius: '8px',
            height: 'calc(90vh + 80px)',
          }}
        >
          <h2
            style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '16px',
            }}
          >
            Improvement Areas
          </h2>

          <div style={{ position: 'relative', marginBottom: '16px' }}>
            {/* Circle container content */}
            {/* ... existing circle container code ... */}
          </div>

          {/* D3 Bubble Chart */}
          <D3BubbleChart
            areas={Object.values(calculatedAreas).flat()}
            onMaturityChange={handleMaturityChange}
            highlightedAreas={ui.highlightedAreas}
            relationshipWeights={ui.relationshipWeights}
            getBackgroundColor={getMaturityColor}
            getBorderColor={getMaturityTextColor}
            onHover={handleHoverChange}
            quadrantDescriptions={data.quadrantDescriptions || {}}
          />
        </div>
      </div>

      {/* Top Improvement Opportunities - moved outside */}
      {topOpportunities.length > 0 && (
        <div
          className="top-improvement-opportunities-container"
          style={{
            padding: '16px',
            borderRadius: '8px',
            marginTop: '24px',
          }}
        >
          <h2
            style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            Top Improvement Opportunities
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(
              topOpportunities as Array<{
                name: string;
                quadrant: string;
                calculatedImpact: number;
                maturityLevel: MaturityLevel;
                maturityScore: number;
                opportunityScore: number;
              }>
            ).map((area, index) => (
              <div
                className="opportunity-container"
                key={area.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600',
                    marginRight: '12px',
                    color: '#4b5563',
                  }}
                >
                  {index + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: '500',
                      color: '#111827',
                      marginBottom: '2px',
                    }}
                  >
                    {area.name}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      fontSize: '12px',
                      color: '#6b7280',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        fontSize: '12px',
                        color: '#6b7280',
                      }}
                    >
                      <span>
                        Impact: {area.calculatedImpact}% (Total: {totalImpact}%)
                      </span>
                      <span
                        style={{
                          color: getMaturityTextColor(area.maturityLevel),
                          fontWeight: '500',
                        }}
                      >
                        {area.maturityLevel}
                      </span>
                      <span>Opportunity Score: {area.opportunityScore.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How This Works Section */}
      <div
        className="how-this-works-container"
        style={{
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '24px',
        }}
      >
        <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>How This Works</h2>
        <div style={{ fontSize: '14px' }}>
          <p style={{ marginBottom: '8px' }}>
            <strong>Circle Size:</strong> Represents the calculated impact percentage of each improvement area.
          </p>
          <p style={{ marginBottom: '8px' }}>
            <strong>Circle Color:</strong> Indicates the maturity level of the improvement area:
          </p>
          <div
            style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '12px',
              marginLeft: '16px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  marginRight: '4px',
                  backgroundColor: '#ffe5e5',
                  border: '1px solid #dc2626',
                }}
              ></div>
              <span style={{ color: '#dc2626' }}>Weak</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  marginRight: '4px',
                  backgroundColor: '#ffecd9',
                  border: '1px solid #ea580c',
                }}
              ></div>
              <span style={{ color: '#ea580c' }}>Emerging</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  marginRight: '4px',
                  backgroundColor: '#fffce6',
                  border: '1px solid #ca8a04',
                }}
              ></div>
              <span style={{ color: '#ca8a04' }}>Established</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  marginRight: '4px',
                  backgroundColor: '#e8f5e8',
                  border: '1px solid #16a34a',
                }}
              ></div>
              <span style={{ color: '#16a34a' }}>Peak</span>
            </div>
          </div>
          <p style={{ marginBottom: '8px' }}>
            <strong>Maturity Controls:</strong> Use the + and - buttons that appear when hovering over a circle to
            adjust its maturity level.
          </p>
          <p style={{ marginBottom: '8px' }}>
            <strong>Bidirectional Relationship:</strong> Changes to star ratings affect improvement area maturity
            levels, and changes to maturity levels affect related business value dimensions. When all dimensions are set
            to 1 star, all improvement areas become Weak. When all dimensions are set to 5 stars, all improvement areas
            become Peak.
          </p>
          <p style={{ marginBottom: '8px' }}>
            <strong>Opportunity Score:</strong> Impact × (4 - Maturity) - higher scores indicate better improvement
            opportunities. Impact is calculated using a base weight modified by maturity level (Weak areas have
            increased weights, Peak areas have slightly reduced weights).
          </p>
          <p style={{ marginBottom: '8px' }}>
            <strong>Detailed Information:</strong> Click the maturity badge that appears when hovering over an
            improvement area to see detailed information including raw score, maturity level, opportunity score, and the
            top business values contributing to its importance.
          </p>
          <p style={{ marginBottom: '8px' }}>
            <strong>Relationship Strength:</strong> When hovering over a business value metric, related improvement
            areas are highlighted with different border thicknesses indicating the strength of the relationship.
          </p>
        </div>
      </div>

      {/* All Business Value Metrics and Improvements Section */}
      <div
        className="metrics-and-improvements-container"
        style={{
          padding: '24px',
          borderRadius: '8px',
          marginBottom: '24px',
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
        }}
      >
        {/* Business Value Metrics */}
        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '12px',
              color: '#334155',
            }}
          >
            Business Value Metrics and Their Relationships
          </h3>

          {Object.entries(getDimensionsByCategory()).map(([category, dimensions]) => (
            <div key={category} style={{ marginBottom: '24px' }}>
              <h4
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '12px',
                  color: '#475569',
                  padding: '4px 8px',
                  backgroundColor: '#f1f5f9',
                  borderRadius: '4px',
                  display: 'inline-block',
                }}
              >
                {category}
              </h4>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '16px',
                }}
              >
                {dimensions.map(dimension => (
                  <div
                    key={dimension.name}
                    style={{
                      padding: '16px',
                      backgroundColor: '#ffffff',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: '600',
                          color: '#1e293b',
                        }}
                      >
                        {dimension.name}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#64748b',
                        marginBottom: '12px',
                      }}
                    >
                      {dimension.description}
                    </div>

                    {/* Related Improvement Areas */}
                    <div style={{ marginTop: '12px' }}>
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#475569',
                          marginBottom: '8px',
                        }}
                      >
                        Related Improvement Areas:
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                        }}
                      >
                        {(data.relationships as Relationship[])
                          .filter(rel => rel.dimension === dimension.name)
                          .sort((a, b) => b.weight - a.weight)
                          .map(rel => {
                            // Find the improvement area in any quadrant
                            let area: ImprovementArea | undefined;
                            for (const quadrantAreas of Object.values(data.improvementAreas)) {
                              area = quadrantAreas.find(a => a.name === rel.area);
                              if (area) break;
                            }
                            const maturityLevel = area?.maturityLevel || 'Weak';
                            const maturityModifiers: Record<MaturityLevel, number> | {} =
                              area?.maturityWeightModifiers || {};
                            return (
                              <div
                                key={rel.area}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '4px',
                                  padding: '8px',
                                  backgroundColor: '#f8fafc',
                                  borderRadius: '4px',
                                  border: '1px solid #e2e8f0',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: getMaturityColor(maturityLevel),
                                      }}
                                    />
                                    <div
                                      style={{
                                        fontSize: '12px',
                                        color: '#475569',
                                        fontWeight: '500',
                                      }}
                                    >
                                      {rel.area}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '12px',
                                      color: '#64748b',
                                      fontWeight: '500',
                                    }}
                                  >
                                    {(rel.weight * 100).toFixed(1)}% weight
                                  </div>
                                </div>
                                <div
                                  style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '4px',
                                    marginTop: '4px',
                                    padding: '4px',
                                    backgroundColor: '#f1f5f9',
                                    borderRadius: '3px',
                                  }}
                                >
                                  {Object.entries(maturityModifiers).map(([level, modifier]) => (
                                    <div
                                      key={level}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '2px 6px',
                                        backgroundColor: level === maturityLevel ? '#e2e8f0' : '#ffffff',
                                        borderRadius: '3px',
                                        fontSize: '11px',
                                        color: '#475569',
                                        border: '1px solid #e2e8f0',
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: '6px',
                                          height: '6px',
                                          borderRadius: '50%',
                                          backgroundColor: getMaturityColor(level as MaturityLevel),
                                        }}
                                      />
                                      <span
                                        style={{
                                          fontWeight: level === maturityLevel ? '500' : '400',
                                        }}
                                      >
                                        {level}
                                      </span>
                                      <span
                                        style={{
                                          color: modifier > 0 ? '#16a34a' : modifier < 0 ? '#dc2626' : '#64748b',
                                          fontWeight: '500',
                                        }}
                                      >
                                        {modifier > 0
                                          ? `+${(modifier * 100).toFixed(1)}%`
                                          : modifier < 0
                                          ? `${(modifier * 100).toFixed(1)}%`
                                          : '0%'}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CSS for the pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.9; }
          }
        `}
      </style>

      {/* Add CSS for hover effect */}
      <style>
        {`
          .tooltip-text {
            display: none;
          }
          div:hover .tooltip-text {
            display: block;
          }
          
          /* New hover-based tooltip system */
          .dimension-tooltip-content {
            max-height: 0;
            overflow: hidden;
            opacity: 0;
            transition: max-height 0.3s ease, opacity 0.3s ease, margin 0.3s ease;
            margin-top: 0;
          }
          
          .dimension-item-with-tooltip:hover {
            background-color: #f9f9f9;
            /* Keep the hover state but don't change the border */
          }
          
          .dimension-item-with-tooltip:hover .dimension-tooltip-content {
            max-height: 100px;
            opacity: 1;
            margin-top: 8px;
            border-top: 1px solid #eee;
            padding-top: 8px;
          }
        `}
      </style>
    </div>
  );
};

export default BusinessValueVisualization;
